import { utc } from 'moment';
import { WESTEROS_MICROSERVICES_ENUM, westerosQuery, westerosMutation } from '@/helpers/westeros';
import {
  getClassroomsByCourse,
  getCourses,
  filterClassroomsByPeriod,
  getCurrentPeriodStartDate,
  getTrialStartDate,
} from '@/store/queries/westerosQueries';
import { FilterClassroomsByPeriodType } from '@/models/enrollments/classroom.model';
import { purgeUserAllAccess } from '@/store/mutations/westerosMutations';

const { max } = Math;

interface TrialData {
  active: boolean;
  alreadyUsedTrial: boolean;
  tracking: boolean;
  clientTime: number;
  serverTimeToEnd: number;
}

export default class Enrollments {
  private static trialData: TrialData;

  private static async loadTrialData() {
    if (this.trialData === undefined) {
      const params = window.location.search
        .substring(1)
        .split('&')
        .map((e) => e.split('='));
      const trialViewParam = params.find(([key]) => key === 'trial-view');

      if (trialViewParam) {
        this.trialData = {
          active: true,
          alreadyUsedTrial: true,
          tracking: !!params.find(([key]) => key === 'tracking'),
          clientTime: new Date().getTime(),
          serverTimeToEnd: (parseFloat(trialViewParam[1]) || 15) * 8.64e7,
        };

        return;
      }

      const {
        data: {
          result: { currentServerDate, trialEndDate, trialActive },
        },
      } = await westerosQuery(WESTEROS_MICROSERVICES_ENUM.ENROLLMENTS, getTrialStartDate);

      this.trialData = {
        active: !!trialActive,
        alreadyUsedTrial: !!trialEndDate,
        tracking: true,
        clientTime: new Date().getTime(),
        serverTimeToEnd:
          utc(trialActive ? trialEndDate : undefined).valueOf() - utc(currentServerDate).valueOf(),
      };
    }
  }

  static async isInTrial(): Promise<boolean> {
    await this.loadTrialData();

    return this.trialData.active;
  }

  static async isInTrialTracking(): Promise<boolean> {
    await this.loadTrialData();

    return this.trialData.tracking;
  }

  static async hasAlreadyUsedTrial(): Promise<boolean> {
    await this.loadTrialData();

    return this.trialData.alreadyUsedTrial;
  }

  static async getTimeToEndOfTrial(): Promise<number> {
    await this.loadTrialData();
    const { clientTime, serverTimeToEnd } = this.trialData;
    const timeSinceRequestToServer = max(0, new Date().getTime() - clientTime);

    return max(0, serverTimeToEnd - timeSinceRequestToServer);
  }

  static async finishTrialAndRemoveAccess(userId: number, attempts = 0): Promise<void> {
    const {
      data: {
        result: { success },
      },
    } = await westerosMutation(WESTEROS_MICROSERVICES_ENUM.ENROLLMENTS, purgeUserAllAccess, {
      userId,
    });

    if (!success && attempts < 3) this.finishTrialAndRemoveAccess(userId, attempts + 1);
  }

  static async getClassroomsByCourse(course: string) {
    if (!course) return undefined;

    const response = await westerosQuery(
      WESTEROS_MICROSERVICES_ENUM.ENROLLMENTS,
      getClassroomsByCourse,
      { course },
    );

    return response.data.getMockedClassroomsByCourse.classrooms;
  }

  static async getCourses() {
    const response = await westerosQuery(WESTEROS_MICROSERVICES_ENUM.ENROLLMENTS, getCourses);

    return response.data.getMockedCourses.courses;
  }

  static async filterClassroomsByPeriod(
    course: string,
    classroomsIds: number[],
  ): Promise<FilterClassroomsByPeriodType[] | undefined> {
    if (!course || classroomsIds.length === 0) return undefined;

    const response = await westerosQuery(
      WESTEROS_MICROSERVICES_ENUM.ENROLLMENTS,
      filterClassroomsByPeriod,
      { course, classroomsIds },
    );

    return response.data.filterClassroomsByPeriod.data;
  }

  static async getCurrentPeriodStartDate(): Promise<string> {
    const {
      data: {
        getCurrentPeriodStartDate: { startDate },
      },
    } = await westerosQuery(WESTEROS_MICROSERVICES_ENUM.ENROLLMENTS, getCurrentPeriodStartDate);

    return startDate;
  }
}
